"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = AsyncSelect;
var _CircularProgress = _interopRequireDefault(require("@mui/material/CircularProgress/CircularProgress.js"));
var _TextField = _interopRequireDefault(require("@mui/material/TextField/TextField.js"));
var _Checkbox = _interopRequireDefault(require("@mui/material/Checkbox/Checkbox.js"));
var _Typography = _interopRequireDefault(require("@mui/material/Typography/Typography.js"));
var _Autocomplete = _interopRequireDefault(require("@mui/material/Autocomplete"));
var _react = _interopRequireWildcard(require("react"));
var _useDebounce = require("use-debounce");
var _CheckBoxOutlineBlank = _interopRequireDefault(require("@mui/icons-material/CheckBoxOutlineBlank"));
var _CheckBox = _interopRequireDefault(require("@mui/icons-material/CheckBox"));
var _Chip = _interopRequireDefault(require("@mui/material/Chip"));
var _jsxRuntime = require("react/jsx-runtime");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const SEARCH_DEBOUNCE_MS = 300;
const icon = /*#__PURE__*/(0, _jsxRuntime.jsx)(_CheckBoxOutlineBlank.default, {
  fontSize: "small",
  sx: {
    color: 'text.primary'
  }
});
const checkedIcon = /*#__PURE__*/(0, _jsxRuntime.jsx)(_CheckBox.default, {
  fontSize: "small"
});
function AsyncSelect(props) {
  const {
    multiple,
    onOptionsRequest,
    onOptions,
    requestStart = 'focus',
    firstPage = 1,
    textFieldProps,
    label,
    onChange,
    onLoad,
    showCheckBoxes = false,
    delayOpenBy,
    showActions = false,
    onApply,
    onCancel,
    optionStyle,
    maxNumberOfTags,
    inputStyleProps,
    placeholder,
    OptionActions,
    labelStyles,
    DropDownActions,
    ...rest
  } = props;
  const [open, setOpen] = (0, _react.useState)(false);
  const [searchText, setSearchText] = (0, _react.useState)('');
  const [debouncedSearchText] = (0, _useDebounce.useDebounce)(searchText, SEARCH_DEBOUNCE_MS);
  const [page, setPage] = (0, _react.useState)(firstPage);
  const [totalRecords, setTotalRecords] = (0, _react.useState)();
  const [totalPaginatedRecords, setTotalPaginatedRecords] = (0, _react.useState)();
  const [previousPage, setPreviousPage] = (0, _react.useState)(-1);
  const [options, setOptions] = (0, _react.useState)([]);
  const [loading, setLoading] = (0, _react.useState)(false);
  const hasAllRecords = totalRecords != null && options.length >= totalRecords;
  const hasAllPaginatedRecords = totalPaginatedRecords != null && options.length >= totalPaginatedRecords;
  const handleChange = (event, value, reason) => {
    setSearchText('');
    onChange?.(event, value, reason);
  };
  (0, _react.useEffect)(() => {
    if (requestStart === 'focus' && !open) return;
    if (requestStart === 'text' && !debouncedSearchText) return;
    if (hasAllRecords) return;
    if (page === previousPage) return;
    let handleCancel;
    setLoading(true);
    onOptionsRequest(debouncedSearchText, page, c => handleCancel = c).then(res => {
      if (!res) return;
      setPreviousPage(page);
      const newOptions = parseOptions(res.options);
      let allOptions = [...options, ...newOptions];
      setOptions(allOptions);
      if (onOptions) onOptions(allOptions, res.totalRecords);
      const recordCount = res.totalRecords ?? newOptions.length;
      setTotalPaginatedRecords(recordCount);
      if (!debouncedSearchText && recordCount !== totalRecords) setTotalRecords(recordCount);
    }).finally(() => {
      setLoading(false);
    });
    return () => handleCancel?.();
  }, [requestStart, open, debouncedSearchText, page, previousPage]);
  (0, _react.useEffect)(() => {
    if (hasAllRecords) return;
    if (requestStart === 'text') setOpen(Boolean(debouncedSearchText));
    init();
  }, [requestStart, debouncedSearchText]);
  (0, _react.useEffect)(() => {
    onLoad?.({
      reset: init
    });
  }, []);
  function init() {
    setPreviousPage(-1);
    setPage(firstPage);
    setOptions([]);
  }
  function handleScroll(event) {
    if (loading || hasAllPaginatedRecords) return;
    const {
      scrollTop,
      clientHeight,
      scrollHeight
    } = event.currentTarget;
    if (scrollTop + clientHeight + 1 > scrollHeight) setPage(s => s + 1);
  }
  function handleOpen(e) {
    if (requestStart === 'text') return;
    if (requestStart === 'mount' && loading) return;
    if (delayOpenBy) {
      setTimeout(() => setOpen(true), delayOpenBy);
      return;
    }
    setOpen(true);
  }
  function handleClose() {
    setOpen(false);
    setSearchText('');
  }
  let combinedOptions = options;
  if (searchText) {
    combinedOptions = [...options, {
      label: searchText,
      value: searchText
    }];
  }
  if (showActions && combinedOptions?.length > 0) {
    const actionOptions = [{
      value: '',
      label: ''
    }, {
      value: '',
      label: '',
      type: 'actions'
    }];
    combinedOptions = [...combinedOptions, ...actionOptions];
  }
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_Autocomplete.default, {
    "data-cy": "autocomplete",
    multiple: multiple,
    inputValue: multiple ? searchText : undefined,
    open: open,
    disableCloseOnSelect: multiple,
    forcePopupIcon: requestStart !== 'text',
    onOpen: handleOpen,
    onClose: handleClose,
    onChange: handleChange,
    ListboxProps: {
      onScroll: handleScroll
    },
    isOptionEqualToValue: (option, value) => option.value === value.value,
    getOptionLabel: option => option.label || option.value || '',
    options: combinedOptions,
    loading: loading,
    renderOption: (props, option, {
      selected
    }) => option.type === 'actions' ? /*#__PURE__*/(0, _jsxRuntime.jsx)(DropDownActions, {}) : /*#__PURE__*/(0, _react.createElement)("li", {
      ...props,
      style: optionStyle,
      key: option.value
    }, showCheckBoxes && /*#__PURE__*/(0, _jsxRuntime.jsx)(_Checkbox.default, {
      icon: icon,
      checkedIcon: checkedIcon,
      style: {
        marginRight: 8
      },
      checked: selected
    }), labelStyles && /*#__PURE__*/(0, _jsxRuntime.jsx)(_Typography.default, {
      sx: labelStyles,
      children: option.label
    }), !labelStyles && option.label, OptionActions ? /*#__PURE__*/(0, _jsxRuntime.jsx)(OptionActions, {
      value: option.value
    }) : null),
    renderInput: params => /*#__PURE__*/(0, _jsxRuntime.jsx)(_TextField.default, {
      ...params,
      variant: "outlined",
      label: props.label ?? '',
      "data-cy": "autocomplete-input",
      placeholder: placeholder,
      InputProps: {
        ...params.InputProps,
        endAdornment: /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
          children: [loading ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_CircularProgress.default, {
            color: "inherit",
            size: 20
          }) : null, params.InputProps.endAdornment]
        })
      },
      sx: {
        '& .MuiInputBase-root': {
          '&.MuiInput-root': {
            '&.MuiInput-underline': {
              '&:before, &:after': {
                borderBottomColor: 'text.primary'
              }
            }
          }
        },
        '& .MuiAutocomplete-endAdornment': {
          '& .MuiSvgIcon-root': {
            color: 'text.primary'
          }
        },
        ...inputStyleProps
      },
      ...textFieldProps
    }),
    onInputChange: (event, newInputValue, reason) => {
      if (reason === 'reset') return;
      setSearchText(newInputValue);
    },
    renderTags: maxNumberOfTags > 0 ? (tagValue, getTagProps) => {
      const tags = tagValue.slice(0, maxNumberOfTags).map((tag, index) => /*#__PURE__*/(0, _jsxRuntime.jsx)(MyChip, {
        ...getTagProps({
          index
        }),
        label: tag.label
      }));
      if (tagValue.length > 1) {
        tags.push( /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
          children: `+${tagValue.length - maxNumberOfTags}`
        }));
      }
      return tags;
    } : null,
    ...rest
  });
}
function parseOptions(options) {
  if (options[0] === 'string') return options.map(o => ({
    value: o,
    label: o
  }));else return options;
}
const MyChip = props => {
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_Chip.default, {
    sx: {
      borderRadius: 24,
      color: '#fff',
      boxSizing: 'border-box',
      height: '18px'
    },
    ...props
  });
};